import React, { useRef } from 'react';
import { UserCurrentTaskResp } from 'domain/payload/response/user/UserViewResp';

interface TaskListViewProps {
  tasks: UserCurrentTaskResp[] | null;
  loading: boolean;
  contentHeight: string;
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const TaskListView: React.FC<TaskListViewProps> = ({
  tasks,
  loading,
  contentHeight,
  currentPage,
  pageSize,
  totalRecords,
  totalPages,
  onPageChange,
  onPageSizeChange
}) => {
  const tasksListRef = useRef<HTMLDivElement>(null);
  const hasTasks = tasks && tasks.length > 0;

  return (
    <>
      <div 
        ref={tasksListRef}
        style={{ 
          height: contentHeight,
          overflowY: 'auto',
          minHeight: '450px'
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading tasks...</span>
            </div>
            <p className="mt-2">Loading tasks...</p>
          </div>
        ) : hasTasks ? (
          <div className="list-group list-group-flush">
            {(tasks || []).map((task, index) => (
              <div key={task.id} className="list-group-item border-0 px-3 py-3">
                <div className="d-flex w-100 justify-content-between align-items-start mb-2">
                  <h6 className="mb-1 fw-semibold">{task.taskName || 'Unnamed Task'}</h6>
                  <small className="text-muted badge bg-light text-dark border">
                    {task.taskType?.value || 'N/A'}
                  </small>
                </div>
                <p className="mb-2 text-muted small">{task.placeName || 'No address'}</p>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    <i className="fa-solid fa-location-dot me-1"></i>
                    {task.latitude?.toFixed(6) || '0'}, {task.longitude?.toFixed(6) || '0'}
                  </small>
                  <small className="text-muted">
                    <span className={`badge ${task.status?.value === 'Completed' ? 'bg-success' : 'bg-warning'}`}>
                      {task.status?.value || 'Unknown'}
                    </span>
                  </small>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center d-flex flex-column justify-content-center align-items-center" style={{ height: '100%' }}>
            <i className="fa-solid fa-clipboard-list text-muted fa-3x mb-2"></i>
            <p className="text-muted">No current tasks</p>
          </div>
        )}
      </div>

      {hasTasks && (
        <div className="d-flex justify-content-end align-items-center p-3 border-top">
          <div className="d-flex align-items-center">
            <div className="me-3">
              <label className="me-2 small">Rows:</label>
              <select
                className="form-select form-select-sm d-inline-block w-auto"
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <span className="me-3 small">
              {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
            </span>

            <nav>
              <ul className="pagination mb-0 pagination-sm">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(1)} style={{ border: 0 }} disabled={currentPage === 1}>
                    <i className="fa fa-angle-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(currentPage - 1)} style={{ border: 0 }} disabled={currentPage === 1}>
                    <i className="fa fa-angle-left"></i>
                  </button>
                </li>

                <li className="page-item disabled">
                  <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                </li>

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(currentPage + 1)} style={{ border: 0 }} disabled={currentPage === totalPages}>
                    <i className="fa fa-angle-right"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(totalPages)} style={{ border: 0 }} disabled={currentPage === totalPages}>
                    <i className="fa fa-angle-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default TaskListView;